import { productCatalogue } from '../../../catalogue/products';

export interface RoleOption {
  slug: string;
  name: string;
  description?: string | null;
}

export interface ProductGroup {
  key: string;
  name: string;
  tagline: string;
  roles: RoleOption[];
}

export interface GroupedRoles {
  base: RoleOption[];
  products: ProductGroup[];
}

export const BASE_ADMIN = 'admin';
export const BASE_MEMBER = 'member';

export function isBaseRole(slug: string): boolean {
  return !slug.includes('-');
}

// Longest catalogue key wins: product ids can contain hyphens (foundry-flow-editor
// -> foundry-flow). Unknown products split at the LAST hyphen — levels are single
// words (viewer/admin/editor), product ids are the multi-word side.
const CATALOGUE_KEYS = productCatalogue.map(p => p.key).sort((a, b) => b.length - a.length);

export function productKey(slug: string): string {
  return CATALOGUE_KEYS.find(k => slug.startsWith(`${k}-`)) ?? slug.slice(0, slug.lastIndexOf('-'));
}

// The level is the slug segment after the "<product>-" prefix (guardian-viewer -> viewer).
export function roleLevel(key: string, slug: string): string {
  return slug.slice(key.length + 1);
}

export function groupRoles(available: RoleOption[]): GroupedRoles {
  const base = available.filter(r => isBaseRole(r.slug));
  const byKey = new Map<string, RoleOption[]>();
  for (const r of available.filter(r => !isBaseRole(r.slug))) {
    const k = productKey(r.slug);
    const list = byKey.get(k) ?? [];
    list.push(r);
    byKey.set(k, list);
  }
  const products = [...byKey.entries()].map(([key, roles]) => {
    const cat = productCatalogue.find(p => p.key === key);
    return {
      key,
      name: cat?.name ?? (key.charAt(0).toUpperCase() + key.slice(1)),
      tagline: cat?.tagline ?? '',
      roles,
    };
  });
  // Order by the product catalogue so the editor matches the rest of the app; unknown products last.
  const order = (key: string) => {
    const i = productCatalogue.findIndex(p => p.key === key);
    return i === -1 ? Number.MAX_SAFE_INTEGER : i;
  };
  products.sort((a, b) => order(a.key) - order(b.key));
  return { base, products };
}

export function withBase(selected: string[], slug: string): string[] {
  return [slug, ...selected.filter(s => !isBaseRole(s))];
}

// The selected level within a product group, or 'none'.
export function productLevel(selected: string[], group: ProductGroup): string {
  const slug = selected.find(s => group.roles.some(r => r.slug === s));
  return slug ? roleLevel(group.key, slug) : 'none';
}

// Set a product group's level (single-select): clears the group's other roles first.
// Clear by product-key prefix, not membership in group.roles, so a stale slug the member still
// holds (dropped from the assignable list) is displaced too rather than doubling up.
export function setProductLevel(selected: string[], group: ProductGroup, level: string): string[] {
  const withoutGroup = selected.filter(s => isBaseRole(s) || productKey(s) !== group.key);
  return level === 'none' ? withoutGroup : [...withoutGroup, `${group.key}-${level}`];
}

export const MODAL_STYLES = {
  content: {
    border: '1px solid var(--border-default)',
    background: 'var(--bg-surface)',
    boxShadow: '0 24px 60px rgba(0,0,0,0.25)',
  },
  body: { padding: 0 },
} as const;
